import { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { useDevice } from '@/hooks/useDeviceType';
import { DesktopShell } from '@/components/desktop/DesktopShell';

const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;
// Breathing room around the map at the most-zoomed-out state
const BORDER_PADDING = 16;
// Double-tap detection thresholds
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_RADIUS = 30;
// Converge venue cluster center (Herrmann Hall, Foundation Cottage, Waypoint, King Hall)
const VENUE_CENTER_X = 0.42;
const VENUE_CENTER_Y = 0.50;
const INITIAL_ZOOM_MULTIPLIER = 1.4;
const DOUBLE_TAP_ZOOM = 2.5;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function computeFit(iw: number, ih: number, cw: number, ch: number, mode: 'contain' | 'width' = 'contain') {
  const usableW = cw - BORDER_PADDING * 2;
  const usableH = ch - BORDER_PADDING * 2;
  if (usableW <= 0 || usableH <= 0) return 1;
  if (mode === 'width') return usableW / iw;
  return Math.min(usableW / iw, usableH / ih);
}

function MapViewer() {
  const { isMobile, isTablet } = useDevice();
  const fitMode = isMobile || isTablet ? 'width' : 'contain';

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [fitScale, setFitScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imgNaturalSize = useRef({ w: 0, h: 0 });
  const containerSize = useRef({ w: 0, h: 0 });
  // Ref mirror of fitScale so callbacks never go stale
  const fitScaleRef = useRef(1);
  const lastPinchDist = useRef<number | null>(null);
  const lastTap = useRef<{ time: number; x: number; y: number } | null>(null);
  const fitModeRef = useRef<'contain' | 'width'>(fitMode);
  fitModeRef.current = fitMode;

  useEffect(() => {
    fitScaleRef.current = fitScale;
  }, [fitScale]);

  // Clamp offset so the map edge (with padding) can't pass the container edge.
  // At fit scale the offset is forced to 0; zoomed in allows panning within bounds.
  const clampOffset = useCallback((ox: number, oy: number, s: number) => {
    const { w: iw, h: ih } = imgNaturalSize.current;
    const { w: cw, h: ch } = containerSize.current;
    if (!iw || !cw) return { x: ox, y: oy };
    const maxX = Math.max(0, (iw * s - (cw - BORDER_PADDING * 2)) / 2);
    const maxY = Math.max(0, (ih * s - (ch - BORDER_PADDING * 2)) / 2);
    return {
      x: clamp(ox, -maxX, maxX),
      y: clamp(oy, -maxY, maxY),
    };
  }, []);

  // Track container size; recompute fit when it changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateSize = () => {
      containerSize.current = { w: el.clientWidth, h: el.clientHeight };
      const { w: iw, h: ih } = imgNaturalSize.current;
      if (!iw || !ih) return;
      const fit = computeFit(iw, ih, el.clientWidth, el.clientHeight, fitModeRef.current);
      fitScaleRef.current = fit;
      setFitScale(fit);
      setScale((prev) => Math.max(prev, fit));
      setOffset((prev) => clampOffset(prev.x, prev.y, Math.max(fitScaleRef.current, fit)));
    };

    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [clampOffset]);

  // Re-clamp offset whenever scale changes
  useEffect(() => {
    setOffset((prev) => clampOffset(prev.x, prev.y, scale));
  }, [scale, clampOffset]);

  // Prevent page scroll when wheeling over the map
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const prevent = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', prevent, { passive: false });
    return () => el.removeEventListener('wheel', prevent);
  }, []);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    imgNaturalSize.current = { w: iw, h: ih };
    const { w: cw, h: ch } = containerSize.current;
    const fit = computeFit(iw, ih, cw, ch, fitModeRef.current);
    fitScaleRef.current = fit;
    setFitScale(fit);

    // Start zoomed in on the Converge venue cluster
    const initialScale = clamp(fit * INITIAL_ZOOM_MULTIPLIER, fit, MAX_SCALE);
    const targetOffsetX = (iw / 2 - iw * VENUE_CENTER_X) * initialScale;
    const targetOffsetY = (ih / 2 - ih * VENUE_CENTER_Y) * initialScale;
    setScale(initialScale);
    setOffset(clampOffset(targetOffsetX, targetOffsetY, initialScale));
  }, [clampOffset]);

  const reset = useCallback(() => {
    setScale(fitScaleRef.current);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setScale((prev) => clamp(prev + ZOOM_STEP, fitScaleRef.current, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => clamp(prev - ZOOM_STEP, fitScaleRef.current, MAX_SCALE));
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setScale((prev) => clamp(prev + delta, fitScaleRef.current, MAX_SCALE));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, offsetX: offset.x, offsetY: offset.y };
  }, [offset]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const raw = {
      x: dragStart.current.offsetX + (e.clientX - dragStart.current.x),
      y: dragStart.current.offsetY + (e.clientY - dragStart.current.y),
    };
    setOffset(clampOffset(raw.x, raw.y, scale));
  }, [isDragging, scale, clampOffset]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleDoubleTap = useCallback((tapX: number, tapY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    if (scale > fitScale * 1.1) {
      // Zoomed in — reset to fit
      setScale(fitScaleRef.current);
      setOffset({ x: 0, y: 0 });
    } else {
      // At fit — zoom to tap point
      const targetScale = clamp(fitScale * DOUBLE_TAP_ZOOM, fitScale, MAX_SCALE);
      const tapRelX = tapX - rect.left - rect.width / 2;
      const tapRelY = tapY - rect.top - rect.height / 2;
      setScale(targetScale);
      setOffset(clampOffset(-tapRelX, -tapRelY, targetScale));
    }
  }, [scale, fitScale, clampOffset]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    if (e.touches.length === 2 && t0 && t1) {
      lastPinchDist.current = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
    } else if (e.touches.length === 1 && t0) {
      const now = Date.now();
      const prev = lastTap.current;
      if (
        prev &&
        now - prev.time < DOUBLE_TAP_DELAY &&
        Math.hypot(t0.clientX - prev.x, t0.clientY - prev.y) < DOUBLE_TAP_RADIUS
      ) {
        e.preventDefault();
        lastTap.current = null;
        handleDoubleTap(t0.clientX, t0.clientY);
        return;
      }
      lastTap.current = { time: now, x: t0.clientX, y: t0.clientY };
      setIsDragging(true);
      dragStart.current = { x: t0.clientX, y: t0.clientY, offsetX: offset.x, offsetY: offset.y };
    }
  }, [offset, handleDoubleTap]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    if (e.touches.length === 2 && lastPinchDist.current !== null && t0 && t1) {
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const ratio = dist / lastPinchDist.current;
      lastPinchDist.current = dist;
      setScale((prev) => clamp(prev * ratio, fitScaleRef.current, MAX_SCALE));
    } else if (e.touches.length === 1 && isDragging && t0) {
      const raw = {
        x: dragStart.current.offsetX + (t0.clientX - dragStart.current.x),
        y: dragStart.current.offsetY + (t0.clientY - dragStart.current.y),
      };
      setOffset(clampOffset(raw.x, raw.y, scale));
    }
  }, [isDragging, scale, clampOffset]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    lastPinchDist.current = null;
  }, []);

  const atFit = scale <= fitScale + 0.01;

  return (
    <div className="h-full flex flex-col">
      {/* Zoom controls */}
      <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-border bg-muted/50 flex-shrink-0">
        <span className="text-xs text-muted-foreground mr-1">{Math.round((scale / fitScale) * 100)}%</span>
        <Button variant="outline" size="icon" onClick={zoomOut} className="h-7 w-7" disabled={atFit}>
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="icon" onClick={zoomIn} className="h-7 w-7" disabled={scale >= MAX_SCALE}>
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="icon" onClick={reset} className="h-7 w-7" disabled={atFit}>
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Map canvas */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden bg-muted/30 relative"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
            transformOrigin: 'center center',
            position: 'absolute',
            top: '50%',
            left: '50%',
            userSelect: 'none',
            transition: isAnimating ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          <img
            src="/campus-map.webp"
            alt="2025 NPS Campus Visitor Map"
            draggable={false}
            onLoad={handleImageLoad}
            style={{ display: 'block', maxWidth: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}

export default function EventMapPage() {
  const { isDesktop } = useDevice();

  if (isDesktop) {
    return (
      <DesktopShell>
        <div className="h-full flex flex-col">
          {/* Page header — matches Industry / Opportunities style */}
          <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
            <h1 className="text-2xl font-bold">Event Map</h1>
          </div>
          <div className="flex-1 overflow-hidden">
            <MapViewer />
          </div>
        </div>
      </DesktopShell>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 bottom-16 flex flex-col bg-background">
      <PageHeader title="Event Map" subtitle="NPS Campus" backPath="/" />
      <div className="flex-1 overflow-hidden">
        <MapViewer />
      </div>
    </div>
  );
}
